import { trackPhoneCall } from "@/lib/googleAds";
import { useEffect } from "react";
import { useRoute } from "wouter";
import { Link } from "wouter";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LocationFAQSection from "@/components/LocationFAQSection";
import NearbyCities from "@/components/NearbyCities";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, MapPin, Star, Check, Paintbrush, Shield, Droplets, Hammer, Palette, Wrench, Sparkles, SprayCan, Building2, Scissors } from "lucide-react";
import StructuredData, { getLocalBusinessSchemaForCity, getServiceAreaSchema } from "@/components/StructuredData";
import { CityQuoteForm } from "@/components/CityQuoteForm";
import { CanonicalTag } from "@/components/CanonicalTag";
import { OpenGraphTags } from "@/components/OpenGraphTags";
import Breadcrumb from "@/components/Breadcrumb";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import GoogleReviews from "@/components/GoogleReviews";

interface CityData {
  slug: string;
  name: string;
  county: string;
  description: string;
  heroTitle: string;
  heroSubtitle: string;
  whyChooseUs: string[];
  localTestimonials: Array<{
    name: string;
    initial: string;
    text: string;
    service: string;
  }>;
  nearbyAreas: string[];
  metaDescription: string;
  metaKeywords: string;
}

const cityData: Record<string, CityData> = {
  "santa-barbara": {
    slug: "santa-barbara",
    name: "Santa Barbara",
    county: "Santa Barbara County",
    description: "Santa Barbara's premier commercial and residential painting contractor serving Downtown, State Street Promenade, Funk Zone, Waterfront District, and all Santa Barbara neighborhoods. Danis Painting Co. specializes in commercial painting for hotels, restaurants, retail stores, office buildings, wine tasting rooms, and historic properties. With over 30 years as Santa Barbara's trusted painting contractor, we've transformed hundreds of State Street businesses, Funk Zone restaurants (The Lark, Loquita, Finney's area), waterfront hotels (Harbor House Inn, Mar Monte, Hilton Beachfront), and downtown office buildings. We understand Santa Barbara's unique coastal climate, Spanish Colonial architecture, and the high aesthetic standards expected by our community. Best of Santa Barbara 2025 Award Winner.",
    heroTitle: "Santa Barbara's #1 Commercial Painting Contractor",
    heroSubtitle: "Serving Santa Barbara businesses for 30+ years: Downtown, State Street, Funk Zone, Waterfront. Expert commercial painting for hotels, restaurants, retail, offices, and wine tasting rooms.",
    whyChooseUs: [
      "30+ years serving Santa Barbara commercial properties",
      "Downtown & State Street retail specialists",
      "Funk Zone restaurant & tasting room expertise",
      "Waterfront hotel painting experience",
      "Best of Santa Barbara 2025 Award Winner",
      "Flexible scheduling for tourist season"
    ],
    localTestimonials: [
      {
        name: "Sarah M.",
        initial: "S",
        text: "Danis Painting transformed our State Street retail storefront. Mark and his team worked evenings to avoid disrupting our business. The quality exceeded our expectations and our customers love the new look!",
        service: "Commercial Retail - State Street"
      },
      {
        name: "Michael T.",
        initial: "M",
        text: "Our Funk Zone restaurant needed a complete interior refresh. Danis Painting completed the work between lunch and dinner service with zero disruption. Professional team that understands hospitality businesses.",
        service: "Restaurant Painting - Funk Zone"
      }
    ],
    nearbyAreas: ["Downtown", "State Street", "Funk Zone", "Waterfront", "Montecito", "Goleta"],
    metaDescription: "Santa Barbara commercial painting contractor. Downtown, State Street, Funk Zone, Waterfront. Hotels, restaurants, retail, offices. 30+ years, Best of 2025. Free quotes.",
    metaKeywords: "Santa Barbara commercial painting contractors, downtown Santa Barbara painter, State Street painting contractor, Funk Zone restaurant painting, hotel painting Santa Barbara, office painting downtown, retail store painting, waterfront business painter, Santa Barbara painting contractors"
  },
  "ventura": {
    slug: "ventura",
    name: "Ventura",
    county: "Ventura County",
    description: "Ventura's premier commercial and residential painting contractor serving Downtown Main Street businesses, Ventura Harbor restaurants and shops, Pacific View Mall, office buildings, and coastal properties. Danis Painting Co. specializes in commercial painting for downtown Ventura Main Street retail stores and restaurants, Ventura Harbor Village shops and dining, Pacific View Mall businesses, and Harbor Boulevard hotels. With over 30 years as Ventura's trusted painting contractor, we understand the unique needs of Ventura County businesses from downtown revitalization projects to harbor district hospitality venues. We serve all of Ventura County including Oxnard, Camarillo, and Ojai.",
    heroTitle: "Ventura's #1 Commercial Painting Contractor",
    heroSubtitle: "Serving Ventura businesses for 30+ years: Downtown Main Street, Ventura Harbor, Pacific View Mall, office buildings. Expert commercial painting for retail, restaurants, hotels, and offices.",
    whyChooseUs: [
      "30+ years serving Ventura commercial properties",
      "Downtown Main Street business district specialists",
      "Ventura Harbor restaurant & shop expertise",
      "Pacific View Mall retail painting experience",
      "Harbor Boulevard hotel painting specialists",
      "Flexible scheduling for business operations"
    ],
    localTestimonials: [
      {
        name: "Robert K.",
        initial: "R",
        text: "Danis Painting transformed our Main Street restaurant. They worked nights and weekends to avoid disrupting our business. Professional crew that understands downtown Ventura's high standards. Highly recommend!",
        service: "Commercial Restaurant - Downtown Ventura"
      },
      {
        name: "Patricia S.",
        initial: "P",
        text: "Our Ventura Harbor shop needed a complete exterior refresh. Danis Painting used marine-grade coatings perfect for the harbor environment. Two years later, still looks perfect despite the salt air!",
        service: "Retail Painting - Ventura Harbor"
      }
    ],
    nearbyAreas: ["Oxnard", "Camarillo", "Ojai", "Port Hueneme", "Santa Paula", "Fillmore"],
    metaDescription: "Ventura commercial painting contractor. Downtown Main Street, Ventura Harbor, Pacific View Mall, office buildings. 30+ years experience. Free quotes.",
    metaKeywords: "Ventura commercial painting contractors, Main Street business painter, Ventura Harbor restaurant painting, Pacific View Mall painting, downtown Ventura office painting, Ventura painting contractor, Ventura County commercial painter"
  },
  "montecito": {
    slug: "montecito",
    name: "Montecito",
    county: "Santa Barbara County",
    description: "Montecito's premier commercial and residential painting contractor serving Coast Village Road boutiques, Upper Village businesses, luxury estates, and Mediterranean villas. Danis Painting Co. specializes in commercial painting for Coast Village Road retail stores and restaurants, Upper Village professional offices, Montecito Village shops, and high-end estate management properties. With over 30 years serving Montecito's finest commercial and residential properties, we understand the unique requirements of luxury painting—premium materials, meticulous craftsmanship, discreet service, and attention to architectural details. We serve all of Montecito from the coast to the foothills.",
    heroTitle: "Montecito's Premier Luxury Commercial & Residential Painting Contractor",
    heroSubtitle: "Serving Montecito businesses for 30+ years: Coast Village Road, Upper Village, luxury estates. Expert commercial and residential painting with premium finishes and discreet, professional service.",
    whyChooseUs: [
      "30+ years serving Montecito commercial & residential properties",
      "Coast Village Road & Upper Village business specialists",
      "Luxury estate & Mediterranean villa expertise",
      "Premium materials & architectural detail mastery",
      "Discreet service for high-end clientele",
      "Flexible scheduling for business operations"
    ],
    localTestimonials: [
      {
        name: "Susan K.",
        initial: "S",
        text: "Outstanding quality and craftsmanship! They did extensive drywall repair and painting in our home. The results were flawless. True professionals in every sense.",
        service: "Drywall & Painting"
      },
      {
        name: "Robert H.",
        initial: "R",
        text: "Danis Painting exceeded our expectations on our estate's exterior refresh. Their attention to detail and respect for our property was exceptional.",
        service: "Exterior Painting"
      }
    ],
    nearbyAreas: ["Santa Barbara", "Summerland", "Carpinteria", "Hope Ranch", "Mission Canyon", "Goleta"],
    metaDescription: "Montecito commercial painting contractor. Coast Village Road, Upper Village, luxury estates, Mediterranean villas. Premium finishes, discreet service. 30+ years. Free quotes.",
    metaKeywords: "Montecito commercial painting contractors, Coast Village Road business painter, Upper Village office painting, Montecito luxury painting, estate painting Montecito, Montecito painting contractor, high-end commercial painting, Montecito Village painter"
  },
  "goleta": {
    slug: "goleta",
    name: "Goleta",
    county: "Santa Barbara County",
    description: "Goleta's premier commercial and residential painting contractor serving UCSB, Camino Real Marketplace, Santa Barbara Airport area, and Old Town Goleta. Danis Painting Co. specializes in commercial painting for offices, retail stores, restaurants, student housing, and apartment complexes. With over 30 years of experience, we understand Goleta's unique commercial needs from property management companies to business districts. We serve major commercial hubs including Costco, Home Depot, and the entire Camino Real shopping center area, as well as UCSB campus buildings and student rental properties managed by Dean Brunner Rentals and St. George & Associates.",
    heroTitle: "Goleta's #1 Commercial Painting Contractor",
    heroSubtitle: "Serving Goleta businesses for 30+ years: UCSB campus, Camino Real Marketplace, Airport District, and Old Town. Expert commercial painting for offices, retail, restaurants, and student housing.",
    whyChooseUs: [
      "30+ years serving Goleta commercial properties",
      "UCSB campus & student housing specialists",
      "Camino Real Marketplace area expertise",
      "Licensed California commercial contractor",
      "Flexible scheduling (evenings/weekends)",
      "Minimal business disruption guarantee"
    ],
    localTestimonials: [
      {
        name: "David L.",
        initial: "D",
        text: "Mark and the Danis Painting crew transformed our apartment complex near UCSB. Very organized, great communication throughout the project. The tenants were impressed with the minimal disruption. Perfect for student housing!",
        service: "Commercial Property - UCSB Area"
      },
      {
        name: "Patricia M.",
        initial: "P",
        text: "Excellent commercial painting service for our Camino Real office. They worked evenings to avoid disrupting our business hours. Very professional team that understands commercial needs.",
        service: "Office Painting - Camino Real"
      }
    ],
    nearbyAreas: ["UCSB Campus", "Isla Vista", "Camino Real Marketplace", "Old Town Goleta", "Santa Barbara Airport", "Ellwood"],
    metaDescription: "Goleta commercial painting contractor. UCSB, Camino Real Marketplace, offices, retail, restaurants. Licensed, 30+ years experience. Evening/weekend scheduling. Free quotes.",
    metaKeywords: "Goleta commercial painting contractors, UCSB painting contractor, Camino Real painter, office painting Goleta, retail store painting, restaurant painting, student housing painter, apartment painting Goleta, commercial painter near UCSB, Goleta painting contractors"
  },
  "carpinteria": {
    slug: "carpinteria",
    name: "Carpinteria",
    county: "Santa Barbara County",
    description: "Carpinteria's premier commercial and residential painting contractor serving Linden Avenue businesses, beach hotels, restaurants, retail stores, and coastal properties. Danis Painting Co. specializes in commercial painting for Carpinteria Plaza (Vons, CVS), downtown Linden Avenue businesses, beach hotels and vacation rentals, and coastal restaurants. With over 30 years as Carpinteria's trusted painting contractor, we understand the unique challenges of coastal climate painting—salt air resistance, moisture protection, and maintaining beautiful finishes in beach town conditions. We serve the entire Carpinteria Valley from downtown to the beach.",
    heroTitle: "Carpinteria's #1 Commercial Painting Contractor",
    heroSubtitle: "Serving Carpinteria businesses for 30+ years: Linden Avenue, Carpinteria Plaza, beach hotels, coastal restaurants. Expert commercial painting with salt-resistant, weather-proof finishes.",
    whyChooseUs: [
      "30+ years serving Carpinteria commercial properties",
      "Linden Avenue business district specialists",
      "Beach hotel & coastal restaurant expertise",
      "Salt-resistant & weather-proof coatings",
      "Carpinteria Plaza retail painting experience",
      "Flexible scheduling for tourist season"
    ],
    localTestimonials: [
      {
        name: "David R.",
        initial: "D",
        text: "Danis Painting transformed our Linden Avenue restaurant. They worked nights to avoid disrupting our business and used specialized coatings that handle the coastal humidity. Professional team that understands hospitality businesses.",
        service: "Commercial Restaurant - Linden Avenue"
      },
      {
        name: "Jennifer M.",
        initial: "J",
        text: "Our beach hotel needed exterior painting that could withstand salt air and constant ocean breeze. Danis Painting used marine-grade coatings and the results are stunning. Two years later, still looks perfect!",
        service: "Beach Hotel Painting - Carpinteria"
      }
    ],
    nearbyAreas: ["Santa Barbara", "Summerland", "Montecito", "Ventura", "Rincon", "Loon Point"],
    metaDescription: "Carpinteria commercial painting contractor. Linden Avenue, Carpinteria Plaza, beach hotels, coastal restaurants. Salt-resistant finishes. 30+ years. Free quotes.",
    metaKeywords: "Carpinteria commercial painting contractors, Linden Avenue business painter, Carpinteria Plaza painting, beach hotel painting, coastal restaurant painting, Carpinteria painting contractor, salt-resistant coatings"
  },
  "ojai": {
    slug: "ojai",
    name: "Ojai",
    county: "Ventura County",
    description: "Ojai's trusted commercial and residential painting contractor serving the Ojai Valley's artistic community, boutique hotels, galleries, restaurants, and distinctive homes. Danis Painting Co. has over 30 years of experience working with Ojai's unique architectural heritage—from Spanish Colonial Revival homes along Ojai Avenue to contemporary hillside retreats in the Upper Ojai Valley. We specialize in painting for the Ojai Valley Inn & Spa, downtown Arcade shops and galleries, Libbey Park area businesses, and the growing number of boutique hotels and wellness retreats that define Ojai's character. Our team understands Ojai's Mediterranean climate with its hot, dry summers and cool winters, selecting premium paints that withstand temperature extremes and intense UV exposure while preserving the natural beauty that makes Ojai a world-renowned destination. We work with local artists, gallery owners, and the Ojai Valley Chamber of Commerce to maintain the community's distinctive aesthetic standards.",
    heroTitle: "Ojai Valley's #1 Commercial & Residential Painting Contractor",
    heroSubtitle: "Serving Ojai businesses and homes for 30+ years: Downtown Arcade, Ojai Avenue, boutique hotels, galleries, wellness retreats. Expert painting that preserves Ojai's artistic character.",
    whyChooseUs: [
      "30+ years serving Ojai Valley commercial & residential properties",
      "Downtown Arcade & Ojai Avenue business specialists",
      "Boutique hotel & wellness retreat painting expertise",
      "Gallery and artist studio painting experience",
      "Heat-resistant & UV-protective coatings for Ojai climate",
      "Flexible scheduling for tourism season operations"
    ],
    localTestimonials: [
      {
        name: "James P.",
        initial: "J",
        text: "Danis Painting did a fantastic job on our Spanish Colonial home near Ojai Avenue. They respected every architectural detail—the corbels, the tile trim, the stucco texture. The finish is beautiful and holds up perfectly in Ojai's summer heat.",
        service: "Exterior Painting - Ojai Avenue"
      },
      {
        name: "Maria G.",
        initial: "M",
        text: "We hired Danis Painting for our downtown Arcade gallery. They worked after hours to avoid disrupting our exhibitions and were incredibly mindful of our artwork. The walls look stunning and the paint quality is gallery-grade.",
        service: "Gallery Painting - Downtown Arcade"
      }
    ],
    nearbyAreas: ["Ventura", "Oak View", "Meiners Oaks", "Mira Monte", "Santa Paula", "Upper Ojai"],
    metaDescription: "Ojai commercial & residential painting contractor. Downtown Arcade, Ojai Avenue, boutique hotels, galleries, wellness retreats. 30+ years, licensed & insured. Free quotes.",
    metaKeywords: "Ojai painting contractor, Ojai painting contractors, Ojai Valley painter, Ojai Avenue business painting, gallery painting Ojai, boutique hotel painting, Spanish Colonial painting Ojai, commercial painter Ojai"
  },
  "camarillo": {
    slug: "camarillo",
    name: "Camarillo",
    county: "Ventura County",
    description: "Camarillo's premier commercial and residential painting contractor serving the Camarillo Premium Outlets area, Las Posas Road business corridor, Old Town Camarillo, and residential communities throughout the city. Danis Painting Co. has over 30 years of experience painting Camarillo's diverse properties—from the Spanish-style homes in Mission Oaks to modern commercial buildings along Ventura Boulevard and Daily Drive. We specialize in commercial painting for retail centers, medical offices along Las Posas Road, industrial facilities in the Camarillo Airport business park, and the growing number of restaurants and shops near the Camarillo Premium Outlets. Our team understands Camarillo's inland climate with warm Santa Ana winds and moderate coastal influence, selecting durable paints that resist fading and cracking in the Ventura County sun. We serve property management companies, HOAs, and business owners throughout Camarillo and the Pleasant Valley area.",
    heroTitle: "Camarillo's #1 Commercial & Residential Painting Contractor",
    heroSubtitle: "Serving Camarillo businesses and homes for 30+ years: Premium Outlets area, Las Posas Road, Old Town, Mission Oaks. Expert commercial and residential painting.",
    whyChooseUs: [
      "30+ years serving Camarillo commercial & residential properties",
      "Premium Outlets area & Las Posas Road business specialists",
      "Old Town Camarillo & Mission Oaks residential expertise",
      "Camarillo Airport business park industrial painting",
      "HOA-approved colors and community compliance",
      "Flexible scheduling for retail and medical office operations"
    ],
    localTestimonials: [
      {
        name: "Richard B.",
        initial: "R",
        text: "Danis Painting completed our Las Posas Road medical office building on time and on budget. They worked evenings and weekends so our patients weren't disrupted. The professional finish really elevated our practice's image.",
        service: "Commercial Office - Las Posas Road"
      },
      {
        name: "Nancy D.",
        initial: "N",
        text: "Our Mission Oaks home needed a complete exterior refresh. Danis Painting helped us choose HOA-approved colors and the results are stunning. The paint has held up beautifully through two summers of Santa Ana winds.",
        service: "Residential Exterior - Mission Oaks"
      }
    ],
    nearbyAreas: ["Oxnard", "Ventura", "Thousand Oaks", "Somis", "Pleasant Valley", "Moorpark"],
    metaDescription: "Camarillo commercial & residential painting contractor. Premium Outlets area, Las Posas Road, Old Town, Mission Oaks. 30+ years, licensed & insured. Free quotes.",
    metaKeywords: "Camarillo painting contractor, Camarillo painting contractors, Las Posas Road painter, Mission Oaks painting, commercial painter Camarillo, Premium Outlets area painting, Old Town Camarillo painter"
  },
  "solvang": {
    slug: "solvang",
    name: "Solvang",
    county: "Santa Barbara County",
    description: "Solvang's premier commercial and residential painting contractor specializing in the village's iconic Danish-inspired architecture, Copenhagen Drive boutiques, Mission Drive restaurants, and Santa Ynez Valley wine tasting rooms. Danis Painting Co. has over 30 years of experience preserving and enhancing Solvang's distinctive half-timbered facades, windmill structures, and traditional Danish color palettes. We serve downtown Solvang's 150+ shops and restaurants, the Solvang Festival Theater area, Hans Christian Andersen Museum district, and the growing wine tasting room scene along Copenhagen Drive and Alisal Road. Our painters are trained in European-style decorative techniques including faux wood graining, traditional Danish color matching, and ornamental trim work that maintains Solvang's authentic village character. We understand the Santa Ynez Valley's warm, dry climate and select UV-resistant, fade-proof paints that keep Solvang's colorful buildings vibrant year-round. As a preferred contractor for the Solvang Conference & Visitors Bureau area businesses, we work around peak tourism schedules to minimize disruption.",
    heroTitle: "Solvang's #1 Danish Architecture Painting Specialists",
    heroSubtitle: "Serving Solvang businesses and homes for 30+ years: Copenhagen Drive, Mission Drive, wine tasting rooms. Expert painting that preserves Solvang's Danish village character.",
    whyChooseUs: [
      "30+ years preserving Solvang's Danish architectural heritage",
      "Copenhagen Drive & Mission Drive business specialists",
      "Wine tasting room & restaurant painting expertise",
      "Traditional Danish color matching & decorative techniques",
      "UV-resistant paints for Santa Ynez Valley climate",
      "Flexible scheduling around peak tourism seasons"
    ],
    localTestimonials: [
      {
        name: "Erik L.",
        initial: "E",
        text: "Danis Painting repainted our Danish-style inn on Copenhagen Drive and did an exceptional job matching the traditional half-timber colors while modernizing the protective finish. Two years later, the colors are still vibrant despite the valley sun.",
        service: "Commercial Inn - Copenhagen Drive"
      },
      {
        name: "Karen M.",
        initial: "K",
        text: "They painted our wine tasting room on Alisal Road and understood exactly how to maintain the Danish village aesthetic while creating a modern, inviting interior. Professional crew that worked around our tasting schedule.",
        service: "Wine Tasting Room - Alisal Road"
      }
    ],
    nearbyAreas: ["Buellton", "Santa Ynez", "Los Olivos", "Ballard", "Santa Barbara", "Lompoc"],
    metaDescription: "Solvang commercial & residential painting contractor. Danish architecture specialists, Copenhagen Drive, Mission Drive, wine tasting rooms. 30+ years. Free quotes.",
    metaKeywords: "Solvang painting contractor, Solvang painting contractors, Danish architecture painter, Copenhagen Drive painting, wine tasting room painter Solvang, Mission Drive business painting, Solvang commercial painter"
  },
  "buellton": {
    slug: "buellton",
    name: "Buellton",
    county: "Santa Barbara County",
    description: "Buellton's trusted commercial and residential painting contractor serving the Highway 101 corridor, Avenue of Flags business district, Industrial Way commercial properties, and the growing wine country hospitality scene. Danis Painting Co. has over 30 years of experience painting Buellton's diverse properties—from the Hitching Post II and other iconic Avenue of Flags restaurants to the Marriott Santa Ynez Valley, Flying Flags RV Resort, and the expanding wine tasting rooms and craft breweries that have transformed Buellton into a Santa Ynez Valley destination. We specialize in commercial painting for hospitality businesses, Highway 101 corridor motels and restaurants, Industrial Way warehouses and production facilities, and the residential neighborhoods of Buellton Village and Rancho de Maria. Our team understands the Santa Ynez Valley's hot, dry summers and selects durable, UV-resistant paints that maintain their color and finish despite intense sun exposure and occasional Santa Ana winds. We work with Buellton's business community to schedule projects around peak tourism and harvest seasons.",
    heroTitle: "Buellton's #1 Commercial & Residential Painting Contractor",
    heroSubtitle: "Serving Buellton businesses and homes for 30+ years: Avenue of Flags, Highway 101 corridor, wine country hospitality. Expert painting for Santa Ynez Valley properties.",
    whyChooseUs: [
      "30+ years serving Buellton & Santa Ynez Valley properties",
      "Avenue of Flags restaurant & hospitality specialists",
      "Highway 101 corridor motel & commercial painting",
      "Industrial Way warehouse & production facility expertise",
      "UV-resistant paints for Santa Ynez Valley climate",
      "Flexible scheduling around tourism & harvest seasons"
    ],
    localTestimonials: [
      {
        name: "Steve R.",
        initial: "S",
        text: "Danis Painting repainted our Avenue of Flags restaurant and worked entirely around our dinner service hours. They finished ahead of schedule and the new exterior has attracted noticeably more walk-in customers from the highway.",
        service: "Restaurant Painting - Avenue of Flags"
      },
      {
        name: "Barbara T.",
        initial: "B",
        text: "Our Buellton Village home needed a complete exterior refresh after years of valley sun. Danis Painting used UV-resistant coatings and the results are beautiful. Three years later, the paint still looks brand new.",
        service: "Residential Exterior - Buellton Village"
      }
    ],
    nearbyAreas: ["Solvang", "Santa Ynez", "Los Olivos", "Santa Barbara", "Lompoc", "Ballard"],
    metaDescription: "Buellton commercial & residential painting contractor. Avenue of Flags, Highway 101 corridor, wine country hospitality. 30+ years, licensed & insured. Free quotes.",
    metaKeywords: "Buellton painting contractor, Buellton painting contractors, Avenue of Flags painter, Highway 101 commercial painting, Santa Ynez Valley painter, Buellton commercial painter, wine country painting"
  },
  "santa-ynez": {
    slug: "santa-ynez",
    name: "Santa Ynez",
    county: "Santa Barbara County",
    description: "Santa Ynez's premier commercial and residential painting contractor serving the valley's renowned horse ranches, luxury estates, wine country properties, and the charming downtown business district. Danis Painting Co. has over 30 years of experience painting Santa Ynez's distinctive properties—from sprawling equestrian estates along Refugio Road to the boutique shops and restaurants on Sagunto Street. We specialize in large-scale ranch and estate painting, including barns, stables, guest houses, main residences, and fencing. Our team has painted properties for some of Santa Ynez Valley's most prominent ranch owners and vineyard estates, understanding the unique requirements of rural properties: weather exposure on all sides, large surface areas requiring efficient crew coordination, and the need for durable agricultural-grade coatings on outbuildings. We also serve Santa Ynez's growing wine country hospitality scene, painting tasting rooms, bed-and-breakfasts, and the restaurants along Sagunto Street that draw visitors from across California. Our painters select premium exterior paints rated for the valley's extreme temperature swings—from 100°F+ summer days to frosty winter mornings.",
    heroTitle: "Santa Ynez Valley's #1 Ranch & Estate Painting Contractor",
    heroSubtitle: "Serving Santa Ynez ranches, estates, and businesses for 30+ years: Refugio Road, Sagunto Street, wine country properties. Expert painting for large-scale rural and luxury properties.",
    whyChooseUs: [
      "30+ years painting Santa Ynez ranches, estates & vineyards",
      "Large-scale property specialists (barns, stables, main homes)",
      "Sagunto Street downtown business painting expertise",
      "Wine tasting room & hospitality property specialists",
      "Agricultural-grade coatings for outbuildings & fencing",
      "Efficient crew coordination for multi-structure properties"
    ],
    localTestimonials: [
      {
        name: "John M.",
        initial: "J",
        text: "Danis Painting handled our entire ranch—main house, guest cottage, barn, and all the fencing along Refugio Road. Their crew was incredibly organized, completing the project in phases without disrupting our horses or daily operations. Outstanding quality.",
        service: "Ranch Property - Refugio Road"
      },
      {
        name: "Elizabeth W.",
        initial: "E",
        text: "We hired Danis Painting for our vineyard estate's main residence and tasting room. They matched the existing Mediterranean palette perfectly and used coatings that withstand our valley's extreme summer heat. Exceptional craftsmanship.",
        service: "Vineyard Estate Painting"
      }
    ],
    nearbyAreas: ["Solvang", "Buellton", "Los Olivos", "Ballard", "Santa Barbara", "Los Alamos"],
    metaDescription: "Santa Ynez commercial & residential painting contractor. Ranches, estates, vineyards, Sagunto Street businesses. 30+ years, licensed & insured. Free quotes.",
    metaKeywords: "Santa Ynez painting contractor, Santa Ynez painting contractors, ranch painter Santa Ynez, estate painting, vineyard painting, Sagunto Street business painter, Santa Ynez Valley commercial painter"
  },
  "isla-vista": {
    slug: "isla-vista",
    name: "Isla Vista",
    county: "Santa Barbara County",
    description: "Isla Vista's premier multi-unit and residential painting contractor serving the UCSB-adjacent community's apartment complexes, student housing, duplexes, and commercial properties along Pardall Road and Embarcadero del Mar. Danis Painting Co. has over 30 years of experience painting Isla Vista's high-turnover rental properties, working with major property management companies including Dean Brunner Rentals, St. George & Associates, Wolfe & Associates, and UCSB's own campus housing facilities. We specialize in rapid-turnaround apartment painting between lease cycles—our crews can repaint a standard 2-bedroom unit in a single day, allowing property managers to minimize vacancy time during the critical September move-in season. We also serve Isla Vista's commercial properties along Pardall Road and Embarcadero del Mar, including restaurants, shops, and the IV Food Co-op. Our team understands the unique challenges of coastal Isla Vista: salt air corrosion on oceanfront Del Playa Drive properties, high-traffic wear in student housing common areas, and the need for durable, washable paints that withstand the demands of rental properties. We offer volume pricing for property managers with multiple units and coordinate multi-building projects efficiently.",
    heroTitle: "Isla Vista's #1 Multi-Unit & Rental Property Painting Contractor",
    heroSubtitle: "Serving Isla Vista property managers and homeowners for 30+ years: Del Playa, Pardall Road, Embarcadero del Mar. Rapid-turnaround apartment painting specialists.",
    whyChooseUs: [
      "30+ years painting Isla Vista multi-unit & rental properties",
      "Rapid-turnaround: full apartment repaint in one day",
      "Preferred contractor for major IV property managers",
      "Del Playa Drive oceanfront property specialists",
      "Volume pricing for multi-building property portfolios",
      "Durable, washable paints for high-traffic rental units"
    ],
    localTestimonials: [
      {
        name: "Property Manager A.",
        initial: "A",
        text: "Danis Painting handles all 47 of our Isla Vista units every September. They coordinate the entire turnover painting schedule, completing each unit in a single day so we never miss a move-in date. Incredibly efficient and reliable year after year.",
        service: "Multi-Unit Turnover - Isla Vista"
      },
      {
        name: "Mark S.",
        initial: "M",
        text: "Our Del Playa duplex needed salt-resistant exterior paint and a complete interior refresh. Danis Painting used marine-grade coatings on the exterior and durable washable paint inside. Two years of student tenants later, it still looks great.",
        service: "Duplex Painting - Del Playa Drive"
      }
    ],
    nearbyAreas: ["Goleta", "UCSB Campus", "Santa Barbara", "Hope Ranch", "Ellwood", "Winchester Canyon"],
    metaDescription: "Isla Vista multi-unit & rental property painting contractor. Rapid-turnaround apartment painting, Del Playa, Pardall Road. 30+ years, volume pricing. Free quotes.",
    metaKeywords: "Isla Vista painting contractor, Isla Vista painting contractors, apartment painter Isla Vista, student housing painting, multi-unit painter, Del Playa painting, rental property painter UCSB"
  },
  "los-angeles": {
    slug: "los-angeles",
    name: "Los Angeles",
    county: "Los Angeles County",
    description: "Extending our award-winning painting services from Santa Barbara County to the greater Los Angeles area. Danis Painting Co. brings 30+ years of honest New England craftsmanship to LA's diverse residential and commercial properties—from Malibu beachfront homes and Santa Monica condominiums to Westside office buildings and Valley commercial properties. We specialize in projects that demand the same meticulous preparation and premium finishes that have made us Santa Barbara's most trusted painting contractor. Our LA-area services include commercial painting for office buildings, retail spaces, restaurants, and multi-unit residential complexes, as well as residential painting for single-family homes, condos, and luxury properties. We use the same premium paints and professional techniques that protect coastal Santa Barbara properties, making us especially well-suited for LA's coastal communities in Malibu, Pacific Palisades, and Santa Monica. Our crews travel from our Santa Barbara headquarters, bringing the work ethic and attention to detail that distinguishes us from typical LA painting contractors. We offer competitive pricing and free on-site estimates for all Los Angeles area projects.",
    heroTitle: "Los Angeles Professional Painting Services by Santa Barbara's #1 Contractor",
    heroSubtitle: "Bringing 30+ years of award-winning Santa Barbara craftsmanship to Los Angeles. Commercial and residential painting for Malibu, Santa Monica, Westside, and Valley properties.",
    whyChooseUs: [
      "30+ years experience – Santa Barbara's award-winning contractor",
      "Malibu & Santa Monica coastal property specialists",
      "Commercial painting for Westside & Valley businesses",
      "Premium materials & meticulous surface preparation",
      "Licensed California contractor with full insurance",
      "Free on-site estimates for all LA-area projects"
    ],
    localTestimonials: [
      {
        name: "Jennifer L.",
        initial: "J",
        text: "We found Danis Painting through a Malibu neighbor's recommendation. They drove from Santa Barbara and the quality difference from LA painters we've used before was immediately obvious. They repaired our deck and applied roof coating—outstanding workmanship and fair pricing.",
        service: "Deck & Roof Coating - Malibu"
      },
      {
        name: "Carlos R.",
        initial: "C",
        text: "Our Westside commercial building needed a complete exterior repaint. Danis Painting stayed on schedule, communicated daily, and delivered a finish that looks better than when the building was new. Worth every penny to get Santa Barbara quality in LA.",
        service: "Commercial Building - Westside LA"
      }
    ],
    nearbyAreas: ["Malibu", "Santa Monica", "Pacific Palisades", "Brentwood", "Westwood", "West Hollywood"],
    metaDescription: "Los Angeles commercial & residential painting contractor. Santa Barbara's #1 painter serving Malibu, Santa Monica, Westside. 30+ years, licensed & insured. Free quotes.",
    metaKeywords: "Los Angeles painting contractor, Los Angeles painting contractors, LA commercial painter, Malibu painting contractor, Santa Monica painter, Westside commercial painting, residential painter Los Angeles"
  },
  "summerland": {
    slug: "summerland",
    name: "Summerland",
    county: "Santa Barbara County",
    description: "Summerland is a charming, tight-knit beach community nestled between Montecito and Carpinteria along the scenic Ventura County coast, known for its antique shops, art galleries, and beautifully preserved Victorian and Craftsman-style homes. With approximately 1,500 residents, Summerland offers a quiet, small-town atmosphere with stunning ocean views and a rich artistic heritage that dates back to its founding in the 1880s as a Spiritualist colony.\n\nDanis Painting Co. has been the trusted painting contractor for Summerland homeowners and businesses for over 30 years. Our team understands the unique character of Summerland's historic homes—many featuring original wood siding, decorative trim, and period-appropriate color palettes that require careful preparation and specialized techniques to maintain their authenticity. We work with homeowners to select colors that honor Summerland's architectural heritage while providing modern protection against the coastal elements.\n\nSummerland's oceanfront location presents specific painting challenges: salt air corrosion, UV exposure, and moisture from coastal fog can rapidly degrade paint finishes. We use premium marine-grade exterior coatings and thorough surface preparation—including hand-scraping, priming, and caulking—to ensure lasting protection for Summerland's coastal properties. Our commercial painting services extend to Summerland's beloved Lillingston Canyon Road antique district, the Big Yellow House restaurant, and the boutique shops along Via Real.\n\nFor residential properties, we specialize in whole-house repaints, deck and fence staining, cabinet refinishing, and interior transformations that complement Summerland's relaxed coastal aesthetic. Our crews respect the quiet neighborhood atmosphere, maintaining clean worksites and coordinating schedules to minimize disruption to residents and their neighbors.",
    heroTitle: "Summerland's Trusted Painting Contractor for Historic Homes & Coastal Properties",
    heroSubtitle: "Serving Summerland homeowners and businesses for 30+ years. Specialists in historic home painting, coastal protection, and antique district commercial services.",
    whyChooseUs: [
      "30+ years painting Summerland's historic homes & businesses",
      "Historic home specialists: Victorian, Craftsman & period colors",
      "Marine-grade coastal protection for oceanfront properties",
      "Antique district & commercial painting services",
      "Premium materials with meticulous surface preparation",
      "Quiet, respectful crews who maintain clean worksites"
    ],
    localTestimonials: [
      {
        name: "Patricia W.",
        initial: "P",
        text: "Our 1920s Craftsman bungalow in Summerland needed a complete exterior repaint. Danis Painting spent two full days on prep alone—hand-scraping, priming every bare spot, and caulking every seam. The result is stunning and the paint has held up beautifully through two winters of coastal weather.",
        service: "Historic Home Exterior - Summerland"
      },
      {
        name: "David K.",
        initial: "D",
        text: "We own a small gallery on Lillingston Canyon Road and Danis Painting transformed our space. They matched the exact period-appropriate colors we wanted and worked around our business hours. Professional, clean, and the quality is exceptional.",
        service: "Commercial Gallery Painting - Summerland"
      },
      {
        name: "Jennifer L.",
        initial: "J",
        text: "Living in Summerland's coastal environment is beautiful but tough on paint. Danis Painting recommended marine-grade coatings and did meticulous prep work. Five years later, our Victorian home's exterior still looks fresh and vibrant. Worth every penny!",
        service: "Victorian Home Exterior - Summerland"
      },
      {
        name: "Thomas R.",
        initial: "T",
        text: "We needed our antique shop interior completely refreshed. Danis Painting's crew was respectful of our inventory, worked efficiently, and the new colors perfectly complement our vintage pieces. Highly recommend for any Summerland business owner.",
        service: "Antique Shop Interior - Summerland"
      }
    ],
    nearbyAreas: ["Montecito", "Carpinteria", "Santa Barbara", "Toro Canyon", "Ortega Ridge", "Padaro Lane"],
    metaDescription: "Summerland painting contractor specializing in historic homes, coastal properties & antique district businesses. 30+ years experience, marine-grade coatings. Free estimates.",
    metaKeywords: "Summerland painting contractor, Summerland house painters, historic home painting Summerland, coastal painting contractor, commercial painting Summerland, Summerland CA painter"
  },
  "oxnard": {
    slug: "oxnard",
    name: "Oxnard",
    county: "Ventura County",
    description: "Oxnard is Ventura County's largest city with over 200,000 residents, featuring a diverse mix of residential neighborhoods, commercial districts, agricultural properties, and the beautiful Channel Islands Harbor and Hollywood Beach communities. As the economic hub of Ventura County, Oxnard offers tremendous demand for professional painting services across its residential subdivisions, commercial corridors along Oxnard Boulevard and Saviers Road, industrial parks, and the growing Collection at RiverPark shopping district.\n\nDanis Painting Co. brings our award-winning Santa Barbara quality to Oxnard's diverse painting needs. Our commercial painting division serves Oxnard's thriving business community—from the Collection at RiverPark retail spaces and Oxnard Financial Plaza office buildings to the restaurants and shops along Channel Islands Boulevard and the industrial facilities in the Del Norte corridor. We understand the scale and scheduling requirements of commercial projects in a busy city like Oxnard, providing detailed project timelines and maintaining clean, safe worksites that minimize disruption to business operations.\n\nFor residential properties, we serve Oxnard's wide range of neighborhoods—from the luxury waterfront homes at Mandalay Bay and Channel Islands Harbor to the established neighborhoods of Oxnard Shores, the Riverpark community, and the historic Henry T. Oxnard district. Our services include interior and exterior painting, cabinet refinishing, deck staining, drywall repair, and specialty coatings. We use premium paints selected for Oxnard's coastal and agricultural climate: UV-resistant formulas for sun-exposed surfaces, moisture-resistant coatings for harbor-area properties, and durable finishes that withstand the salt air common throughout the city.\n\nOur roof coating and waterproofing services are especially popular in Oxnard, where flat-roofed commercial buildings and stucco-clad homes benefit from reflective, waterproof coatings that reduce cooling costs and extend roof life. We also provide pressure washing services to remove the agricultural dust and salt buildup that accumulates on Oxnard properties throughout the year.",
    heroTitle: "Oxnard's Premier Commercial & Residential Painting Contractor",
    heroSubtitle: "Serving Oxnard homes and businesses with award-winning quality. Channel Islands Harbor, RiverPark, Oxnard Shores, and all Oxnard neighborhoods. 30+ years experience.",
    whyChooseUs: [
      "30+ years experience – award-winning Santa Barbara contractor",
      "Commercial painting for RiverPark, Financial Plaza & more",
      "Channel Islands Harbor & Mandalay Bay waterfront specialists",
      "Roof coating & waterproofing for Oxnard's climate",
      "Licensed California contractor with full insurance",
      "Free on-site estimates for all Oxnard properties"
    ],
    localTestimonials: [
      {
        name: "Roberto M.",
        initial: "R",
        text: "Danis Painting repainted our entire office building on Oxnard Boulevard—three stories, including the parking structure. They stayed on schedule, communicated every step, and the building looks brand new. Several neighboring businesses have asked for their contact information.",
        service: "Commercial Building - Oxnard Boulevard"
      },
      {
        name: "Sarah T.",
        initial: "S",
        text: "Our Channel Islands Harbor home needed salt-resistant exterior paint and a complete interior refresh. Danis Painting recommended marine-grade coatings and the results have been outstanding. Two years later, the paint still looks perfect despite the harbor conditions.",
        service: "Harbor Home Painting - Channel Islands"
      },
      {
        name: "Michael C.",
        initial: "M",
        text: "Our RiverPark retail space needed a modern makeover. Danis Painting understood our vision and delivered exactly what we wanted. The crew was professional, minimized disruption to our business, and the quality is outstanding. We've already recommended them to other RiverPark tenants.",
        service: "Retail Space - RiverPark Collection"
      },
      {
        name: "Linda H.",
        initial: "L",
        text: "We own a Mandalay Bay waterfront property and Danis Painting's expertise with marine-grade coatings was invaluable. They understood the unique challenges of harbor properties and delivered a finish that looks beautiful and will last. Exceptional service!",
        service: "Waterfront Home - Mandalay Bay"
      }
    ],
    nearbyAreas: ["Ventura", "Camarillo", "Port Hueneme", "Channel Islands Harbor", "Mandalay Bay", "RiverPark"],
    metaDescription: "Oxnard commercial & residential painting contractor. Channel Islands Harbor, RiverPark, Oxnard Shores. 30+ years, licensed & insured. Free estimates.",
    metaKeywords: "Oxnard painting contractor, Oxnard house painters, commercial painting Oxnard, Channel Islands Harbor painter, Oxnard CA painting services, residential painter Oxnard"
  }
};

const cityHeroImages: Record<string, string> = {
  "buellton": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/blog-buellton-painting_7f798c98.jpg",
  "camarillo": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/blog-camarillo-painters_1e90c20e.jpg",
  "carpinteria": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/blog-carpinteria-coastal_8e0279fa.jpg",
  "goleta": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/city-goleta-hero-v4-BsLb3MTk9jdEf2JqHqSJYn_55e100bd.webp",
  "isla-vista": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/blog-isla-vista-painting_8eeecf0b_d3bccf34.webp",
  "los-angeles": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/city-los-angeles-hero-Q43CnMrvifYpJYq6vfeL4m_87685364.webp",
  "montecito": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/city-montecito-hero-v3-RFGTgpGoapLYSBhbEWkmoB_143275f7.webp",
  "ojai": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/blog-ojai-painting_445ffda3_376f8d7b.webp",
  "oxnard": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/city-oxnard-hero-v3-7mhgHSc7vsaDiSy836jhtp_932798e9.webp",
  "santa-barbara": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/hero-santa-barbara_8efb2d2c_41509eb6.webp",
  "santa-ynez": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/blog-santa-ynez-painting_dad445ea_8e21f6d4.webp",
  "solvang": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/blog-solvang-painting_8cbe508d_337b97a5.webp",
  "summerland": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/blog-summerland-coastal_3e30efbc_ace43cfd.webp",
  "ventura": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/blog-ventura-painting_486e0105_cafd3961.webp"
};

export default function CityLandingPage() {
  const [match, params] = useRoute("/city/:slug");
  const canonicalUrl = params?.slug ? `https://danispainting.net/city/${params.slug}` : 'https://danispainting.net/';
  const citySlug = params?.slug || "";
  const city = cityData[citySlug];

  useEffect(() => {
    if (city) {
      document.title = `${city.heroTitle} | Danis Painting Co.`;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', city.metaDescription);
      
      // Update meta keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', city.metaKeywords);
    }
  }, [city, canonicalUrl]);

  if (!match || !city) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container py-20 text-center">
          <h2 className="text-3xl font-bold mb-4">Page Not Found</h2>
          <p className="text-gray-600 mb-6">The page you're looking for doesn't exist.</p>
          <Link href="/">
            <Button>Back to Home</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const cityPageUrl = `https://danispainting.net/city/${city.slug}`;
  const localBusinessSchema = getLocalBusinessSchemaForCity(city.slug, city.name, cityPageUrl);
  const serviceAreaSchema = getServiceAreaSchema(city.slug, city.name);

  return (
    <div className="min-h-screen bg-white">
      <CanonicalTag url={canonicalUrl} />
      <OpenGraphTags
        title={`${city.heroTitle} | Danis Painting Co.`}
        description={city.metaDescription}
        image="https://danispainting.net/logo.png"
        url={cityPageUrl}
        type="article"
      />
      <StructuredData type="LocalBusiness" data={localBusinessSchema} />
      <StructuredData type="ServiceArea" data={serviceAreaSchema} />
      <Header />

      <BreadcrumbSchema items={[
        { label: "Home", url: "/" },
        { label: "Service Areas", url: "/service-areas" },
        { label: city.name, url: `/city/${city.slug}` }
      ]} />

      {/* Hero Section */}
      <section className="relative text-white pt-32 pb-16 md:pt-36 md:pb-20 overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1e3a5f 50%, #0a1628 100%)' }}>
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${cityHeroImages[citySlug] || 'https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/hero-bg_5849f6b9_e05061fa.webp'})` }}></div>
        <div className="container px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-medium">Serving {city.name}, {city.county}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {city.heroTitle}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8">
              {city.heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:805-403-8727" onClick={trackPhoneCall}
                className="inline-flex items-center justify-center px-8 py-4 bg-[#4caf30] text-white font-semibold rounded-lg hover:bg-[#3d9426] transition-colors"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call: 805-403-8727
              </a>
              <button
                onClick={() => window.dispatchEvent(new Event('openChat'))}
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container px-4 py-3">
          <Breadcrumb items={[
            { label: "Home", url: "/" },
            { label: "Service Areas", url: "/service-areas" },
            { label: city.name, url: `/city/${city.slug}` }
          ]} />
        </div>
      </div>

      {/* About Section */}
      <section className="py-16 bg-gray-50">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-6 text-center">
              Professional Painting Services in {city.name}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              {city.description}
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {city.whyChooseUs.map((reason, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#4caf30] flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{reason}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="container px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-12 text-center">
            Our Services in {city.name}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 max-w-7xl mx-auto">
            <Link href={`/service/interior-exterior-painting/${city.slug}`} className="group">
              <Card className="h-full hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#1e3a8a]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1e3a8a] transition-colors">
                    <Paintbrush className="w-8 h-8 text-[#1e3a8a] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-[#1e3a8a] mb-2">Interior & Exterior Painting</h3>
                  <p className="text-xs text-gray-600">Professional painting services</p>
                </CardContent>
              </Card>
            </Link>
            <Link href={`/service/commercial-painting/${city.slug}`} className="group">
              <Card className="h-full hover:shadow-xl transition-shadow border-2 border-[#0066cc]">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#0066cc]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#0066cc] transition-colors">
                    <Building2 className="w-8 h-8 text-[#0066cc] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-[#0066cc] mb-2">Commercial Painting</h3>
                  <p className="text-xs text-gray-600">Business & office painting</p>
                </CardContent>
              </Card>
            </Link>
            <Link href={`/service/exterior-painting/${city.slug}`} className="group">
              <Card className="h-full hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#1e3a8a]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1e3a8a] transition-colors">
                    <Palette className="w-8 h-8 text-[#1e3a8a] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-[#1e3a8a] mb-2">Exterior Painting</h3>
                  <p className="text-xs text-gray-600">Weather-resistant finishes</p>
                </CardContent>
              </Card>
            </Link>
            <Link href={`/service/cabinet-painting/${city.slug}`} className="group">
              <Card className="h-full hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#1e3a8a]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1e3a8a] transition-colors">
                    <Wrench className="w-8 h-8 text-[#1e3a8a] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-[#1e3a8a] mb-2">Cabinet Painting</h3>
                  <p className="text-xs text-gray-600">Kitchen & bath refinishing</p>
                </CardContent>
              </Card>
            </Link>
            <Link href={`/service/roof-coating/${city.slug}`} className="group">
              <Card className="h-full hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#1e3a8a]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1e3a8a] transition-colors">
                    <Shield className="w-8 h-8 text-[#1e3a8a] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-[#1e3a8a] mb-2">Roof Coating & Repair</h3>
                  <p className="text-xs text-gray-600">Flame-retardant protection</p>
                </CardContent>
              </Card>
            </Link>
            <Link href={`/service/waterproofing/${city.slug}`} className="group">
              <Card className="h-full hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#1e3a8a]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1e3a8a] transition-colors">
                    <Droplets className="w-8 h-8 text-[#1e3a8a] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-[#1e3a8a] mb-2">Waterproofing</h3>
                  <p className="text-xs text-gray-600">Water damage protection</p>
                </CardContent>
              </Card>
            </Link>
            <Link href={`/service/pressure-washing/${city.slug}`} className="group">
              <Card className="h-full hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#1e3a8a]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1e3a8a] transition-colors">
                    <SprayCan className="w-8 h-8 text-[#1e3a8a] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-[#1e3a8a] mb-2">Pressure Washing</h3>
                  <p className="text-xs text-gray-600">Surface cleaning & prep</p>
                </CardContent>
              </Card>
            </Link>
            <Link href={`/service/drywall-repair/${city.slug}`} className="group">
              <Card className="h-full hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#1e3a8a]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1e3a8a] transition-colors">
                    <Hammer className="w-8 h-8 text-[#1e3a8a] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-[#1e3a8a] mb-2">Drywall Repair</h3>
                  <p className="text-xs text-gray-600">Expert patching & finishing</p>
                </CardContent>
              </Card>
            </Link>
            <Link href={`/service/deck-staircase-repair/${city.slug}`} className="group">
              <Card className="h-full hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#1e3a8a]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1e3a8a] transition-colors">
                    <Sparkles className="w-8 h-8 text-[#1e3a8a] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-[#1e3a8a] mb-2">Deck & Staircase Repair</h3>
                  <p className="text-xs text-gray-600">Wood restoration & repair</p>
                </CardContent>
              </Card>
            </Link>
            <Link href={`/service/trim-molding/${city.slug}`} className="group">
              <Card className="h-full hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#1e3a8a]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1e3a8a] transition-colors">
                    <Scissors className="w-8 h-8 text-[#1e3a8a] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-[#1e3a8a] mb-2">Trim & Molding</h3>
                  <p className="text-xs text-gray-600">Detail work & finishing</p>
                </CardContent>
              </Card>
            </Link>
            <Link href={`/service/venetian-plaster/${city.slug}`} className="group">
              <Card className="h-full hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#1e3a8a]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1e3a8a] transition-colors">
                    <Sparkles className="w-8 h-8 text-[#1e3a8a] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-[#1e3a8a] mb-2">Venetian Plaster</h3>
                  <p className="text-xs text-gray-600">Luxury specialty finishes</p>
                </CardContent>
              </Card>
            </Link>
            <Link href={`/service/lime-wash/${city.slug}`} className="group">
              <Card className="h-full hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#1e3a8a]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1e3a8a] transition-colors">
                    <Palette className="w-8 h-8 text-[#1e3a8a] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-[#1e3a8a] mb-2">Lime Wash</h3>
                  <p className="text-xs text-gray-600">Mineral finish & patina</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Nearby Areas Section */}
      <section className="py-16">
        <div className="container px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-8 text-center">
            We Also Serve Areas Near {city.name}
          </h2>
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {city.nearbyAreas.map((area, index) => (
              <div key={index} className="px-6 py-3 bg-gray-100 rounded-lg text-gray-700 font-medium">
                {area}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location-Specific FAQ Section */}
      <LocationFAQSection citySlug={citySlug} cityName={city.name} />

      {/* Nearby Cities Widget */}
      <NearbyCities 
        currentCitySlug={citySlug}
      />

      {/* Google Reviews Section - Real verified reviews from Google Business Profile */}
      <GoogleReviews maxReviews={3} variant="light" />

      {/* Quote Request Form */}
      <CityQuoteForm cityName={city.name} />

      <Footer />
    </div>
  );
}
